import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Button, PasswordField, TextField, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useValidationRules } from '~/hooks/useValidationRules/useValidationRules';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/error-response/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { LoginMethod, useLoginTelemetry } from '~/loginTelemetry/useLoginTelemetry';
import { FormSeparatorHorizontal } from '~/pages/unAuthorizedPages/components/FormSeparator';
import { usePostAuthRedirect } from '~/utils/auth-redirect';
import { ErrorMessage, FooterLink, FormContainer, FormFields, VerticalFormFields, FormSubTitle, SubmitButtonField, LinkButton } from '../../components/FormComponents';
import sharedStyles from '../LoginScreen.module.scss';
import styles from './Password.module.scss';
const passwordTextsPath = 'auth.screens.password';
export const GetPasswordStepTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            PasswordStepTID: {
                Form: 'login-form',
                EmailInput: 'email-input',
                PasswordInput: 'password-input',
                ResetPasswordLink: 'reset-password-link',
                ErrorMessage: 'err-message',
                LoginButton: 'login-button',
                RequestOtpButton: 'request-otp-button',
                BackLink: 'back-link'
            }
        }
    });
export default function PasswordStep(param) {
    let { email, onClickRequestOTP, titleMP, subtitleMP } = param;
    const { t: passwordTexts } = useLocaleKeys(passwordTextsPath);
    const { emailValidation } = useValidationRules();
    const { PasswordStepTID } = useMemo(()=>GetPasswordStepTID(), []);
    const [errorMessage, setErrorMessage] = useState();
    const { handleSubmit, control, formState, setFocus } = useForm({
        mode: 'onChange'
    });
    const { api, logBI } = useStoreActions();
    const [isLoading, setIsLoading] = useState(false);
    const { reportFormSubmission } = useEventReporter();
    const getUtms = useUtmsParams();
    const { browser: { getOrigin } } = useIoContext();
    const { pathname } = useLocation();
    const performPostAuthRedirect = usePostAuthRedirect();
    const { loginSucceeded, loginFailed } = useLoginTelemetry();
    async function onPasswordFormSubmit(param) {
        let { email, password } = param;
        logBI(10254, {});
        setErrorMessage(undefined);
        setIsLoading(true);
        const result = await handleRequest(api.auth.loginWithPassword({
            email,
            password,
            utms: getUtms(),
            anchorReferrer: getOrigin() + pathname
        }));
        if (!result.success || !result.data.authToken) {
            var _result_error;
            if ((_result_error = result.error) === null || _result_error === void 0 ? void 0 : _result_error.hasServerError(ServerError.AccountTemporarilyLocked)) {
                setErrorMessage(passwordTexts.accountLocked());
            } else {
                setErrorMessage(passwordTexts.genericError());
            }
            loginFailed(LoginMethod.Password);
            setIsLoading(false);
        } else {
            reportFormSubmission('general-logged-in-successfully', null);
            loginSucceeded(LoginMethod.Password);
            performPostAuthRedirect();
        }
    }
    useEffect(()=>{
        setFocus('password');
    }, []);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs(FormContainer, {
                onSubmit: handleSubmit(onPasswordFormSubmit),
                "data-testid": PasswordStepTID.Form(),
                children: [
                    titleMP,
                    subtitleMP,
                    /*#__PURE__*/ _jsx(FormFields, {
                        children: /*#__PURE__*/ _jsxs(VerticalFormFields, {
                            children: [
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "email",
                                    defaultValue: email,
                                    rules: emailValidation(undefined, {
                                        required: true
                                    }),
                                    render: (param)=>{
                                        let { field: { onChange, onBlur, ref, value }, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(TextField, {
                                            bi: {
                                                name: 'login_password_email'
                                            },
                                            collapseHelperText: false,
                                            type: "email",
                                            label: passwordTexts.emailLabel(),
                                            inputRef: ref,
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            testId: PasswordStepTID.EmailInput(),
                                            value: value,
                                            onBlur: onBlur,
                                            onChange: onChange,
                                            placeholder: passwordTexts.emailPlaceholder(),
                                            disabled: isLoading,
                                            autoComplete: "username"
                                        });
                                    }
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "password",
                                            defaultValue: "",
                                            rules: requiredValidation(true),
                                            render: (param)=>{
                                                let { field: { onChange, onBlur, ref, value }, fieldState } = param;
                                                var _fieldState_error;
                                                return /*#__PURE__*/ _jsx(PasswordField, {
                                                    bi: {
                                                        name: 'login_password'
                                                    },
                                                    label: passwordTexts.label(),
                                                    inputRef: ref,
                                                    value: value,
                                                    testId: PasswordStepTID.PasswordInput(),
                                                    error: !!fieldState.error,
                                                    helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                                    onBlur: onBlur,
                                                    onChange: onChange,
                                                    placeholder: passwordTexts.placeholder(),
                                                    disabled: isLoading,
                                                    autoComplete: "current-password"
                                                });
                                            }
                                        }),
                                        /*#__PURE__*/ _jsxs(Typography, {
                                            className: styles.forgotYourPassword,
                                            as: "p",
                                            variant: "paragraph2",
                                            skin: "secondary",
                                            children: [
                                                passwordTexts.forgotPassword(),
                                                ' ',
                                                /*#__PURE__*/ _jsx(FooterLink, {
                                                    toInternalLink: routes.resetPassword,
                                                    testId: PasswordStepTID.ResetPasswordLink(),
                                                    children: passwordTexts.resetItHere()
                                                })
                                            ]
                                        }),
                                        errorMessage && /*#__PURE__*/ _jsx(ErrorMessage, {
                                            testId: PasswordStepTID.ErrorMessage(),
                                            children: errorMessage
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SubmitButtonField, {
                                    disabled: !formState.isValid,
                                    isLoading: isLoading,
                                    testId: PasswordStepTID.LoginButton(),
                                    children: passwordTexts.signIn()
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FormSeparatorHorizontal, {
                children: passwordTexts.or()
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.otpContainer,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'request_otp_login'
                        },
                        testId: PasswordStepTID.RequestOtpButton(),
                        skin: "primaryOutline",
                        onClick: ()=>onClickRequestOTP(),
                        children: passwordTexts.getOneTimeCode()
                    }),
                    /*#__PURE__*/ _jsx(FormSubTitle, {
                        children: passwordTexts.otpDescription()
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: sharedStyles.footer,
                children: /*#__PURE__*/ _jsx(LinkButton, {
                    toInternalLink: routes.login,
                    testId: PasswordStepTID.BackLink(),
                    className: sharedStyles.backButton,
                    children: passwordTexts.back()
                })
            })
        ]
    });
}
